import { redirect } from "@tanstack/react-router";
import { isHiddenB2CPath } from "@/config/featureFlags";
import { Suspense, useEffect } from "react";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useLocation,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "@/lib/helmet-compat";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { LifeMartProvider } from "@/hooks/useLifeMart";
import ScrollToTop from "@/components/ScrollToTop";
import RouteMetaGuard from "@/components/RouteMetaGuard";
import MemberPreviewBanner from "@/components/MemberPreviewBanner";
import HomieBubble from "@/components/HomieBubble";
import MobileBottomNav from "@/components/MobileBottomNav";
import DataGraphPageTracker from "@/components/DataGraphPageTracker";
import AffiliateTracker from "@/components/AffiliateTracker";
import AttributionTracker from "@/components/AttributionTracker";
import NotFound from "@/pages/NotFound";
import { Navigate } from "@/lib/router-compat";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { initWebVitals } from "@/lib/webVitals";
import appCss from "../styles.css?url";
import montserratFontUrl from "@/assets/fonts/montserrat-variable.woff2?url";
import montserratItalicFontUrl from "@/assets/fonts/montserrat-italic400.woff2?url";
import robotoSlabFontUrl from "@/assets/fonts/robotoslab-variable.woff2?url";

// ── Ported from index.html ──────────────────────────────────────────────

const GTM_SNIPPET = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-WXBSQNR');`;

// Montserrat + Roboto Slab used to load async from Google Fonts (a JS-
// injected <link>, to dodge a Lighthouse render-blocking-requests finding).
// That traded render-blocking for a real, measured CLS bug: the swap from
// whatever placeholder font was in play to the real webfont shifted layout,
// most consistently traced to the async local()-font-matching path used by
// an earlier attempted fix, not the network fetch itself (confirmed via a
// FontFace-level trace + a real control experiment against the actual
// Cloudflare Workers build: 7/8 runs regressed on the original code, 3-4/8
// still regressed on the local()-fallback attempt). Self-hosting the actual
// variable-font files (below) removes both the external network dependency
// and the local()-matching race in one move -- the real font is now a
// same-origin, Vite-fingerprinted, preloadable ~35KB file per family.

// Loads the full app stylesheet without blocking first paint, same
// JS-injected-<link> pattern GTM_SNIPPET uses below. Safe to defer because
// CRITICAL_CSS (below)
// already inlines the root CSS variables and base body/header rules every
// Tailwind utility class depends on, so the page is never visually broken
// or wrongly colored during the gap -- only non-critical utility classes
// (spacing, below-the-fold layout) finish applying a beat later.
const APP_CSS_ASYNC_SNIPPET = `(function(){var l=document.createElement('link');l.rel='stylesheet';l.href='${appCss}';document.head.appendChild(l);})();`;

// AEO/GEO: AI-readable site identity and capability graph
const SITE_JSON_LD = JSON.stringify([
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Home Approach",
    url: "https://www.homeapproach.com/",
    description:
      "Free homeownership tools, expert guides, and education for buyers, owners, sellers, and investors.",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.homeapproach.com/?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": ["Organization", "FinancialService"],
    name: "Home Approach",
    alternateName: "HomeApproach",
    url: "https://www.homeapproach.com/",
    logo: {
      "@type": "ImageObject",
      url: "https://www.homeapproach.com/og-image.png",
      width: 1200,
      height: 630,
    },
    description:
      "Home Approach provides free homeownership tools, mortgage calculators, grant-matching, AI guidance (Homie), and expert education to help first-time buyers, homeowners, sellers, and investors at every stage.",
    foundingDate: "2023",
    email: "info@homeapproach.com",
    areaServed: "US",
    serviceType: "Homeownership Education and Tools",
    knowsAbout: [
      "First-time homebuying",
      "Mortgage pre-approval",
      "FHA loans",
      "VA loans",
      "Down payment assistance",
      "Mortgage calculators",
      "Home equity",
      "Refinancing",
      "Real estate investing",
      "Home maintenance",
      "Credit score improvement",
      "Grant programs for homebuyers",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Free Homeownership Tools",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mortgage Calculator", url: "https://www.homeapproach.com/mortgage-calculators" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Home Approach Stack", url: "https://www.homeapproach.com/stack" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Homie AI Guide", url: "https://www.homeapproach.com/homie" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Affordability Simulator", url: "https://www.homeapproach.com/affordability-simulator" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Down Payment Planner", url: "https://www.homeapproach.com/down-payment-planner" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Neighborhood Explorer", url: "https://www.homeapproach.com/neighborhood-explorer" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Rent vs Buy Calculator", url: "https://www.homeapproach.com/rent-vs-buy" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Refinance Optimizer", url: "https://www.homeapproach.com/refi-optimizer" } },
      ],
    },
    sameAs: ["https://twitter.com/HomeApproach"],
  },
]);

// Critical CSS for homepage hero (LCP path). Coupled to `data-hero-*`
// attributes in src/components/home/HonestHero.tsx.
// The site-wide slice of CRITICAL_CSS (root CSS variables + body/header base)
// exists so the main stylesheet (appCss, below) can be safely loaded
// asynchronously without a flash of unstyled/wrongly-colored content: every
// Tailwind utility class that reads var(--background)/var(--foreground)/etc.
// would otherwise resolve to nothing until the deferred stylesheet finishes.
// Values are the real computed output of the live theme (captured via
// getComputedStyle against a running page), not hand-typed approximations.
const CRITICAL_CSS = `:root{--background:0 0% 96%;--foreground:30 7% 17%;--primary:223 29% 30%;--primary-foreground:0 0% 100%;--muted-foreground:30 7% 40%;--border:25 10% 88%;--card:0 0% 100%}
@font-face{font-family:'Montserrat';src:url(${montserratFontUrl}) format('woff2');font-weight:100 900;font-style:normal;font-display:swap}
@font-face{font-family:'Montserrat';src:url(${montserratItalicFontUrl}) format('woff2');font-weight:400;font-style:italic;font-display:swap}
@font-face{font-family:'Roboto Slab';src:url(${robotoSlabFontUrl}) format('woff2');font-weight:100 900;font-style:normal;font-display:swap}
html,body{margin:0;padding:0;font-family:Montserrat,ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;-webkit-font-smoothing:antialiased;background:hsl(var(--background));color:hsl(var(--foreground))}
header{position:sticky;top:0;z-index:40;background:hsla(0,0%,100%,.95);border-bottom:1px solid hsl(var(--border))}
h1{font-family:Montserrat,sans-serif;font-weight:700;line-height:1.1}
[data-hero-section]{position:relative;isolation:isolate;overflow:hidden;background:#3a4d6e;color:#f7f2ea;padding:2.5rem 0 1.75rem;min-height:auto}
@media (min-width:768px){[data-hero-section]{padding:4rem 0 2.5rem;min-height:600px}}
[data-hero-bg]{position:absolute;inset:0;z-index:-2;width:100%;height:100%;object-fit:cover;object-position:center 60%}
[data-hero-scrim]{position:absolute;inset:0;z-index:-1;background:linear-gradient(180deg,rgba(58,77,110,.94) 0%,rgba(58,77,110,.90) 55%,rgba(58,77,110,.96) 100%)}
[data-hero-h1]{font-family:Georgia,"Times New Roman",serif;color:#f7f2ea;font-weight:400;line-height:1.12;margin:0 0 1.25rem;font-size:clamp(2rem,4.5vw,3.75rem)}
[data-hero-proof]{display:flex;align-items:center;gap:.5rem;font-size:.875rem;color:rgba(247,242,234,.85);margin:0 0 1.25rem}
[data-hero-cta]{display:inline-flex;align-items:center;justify-content:center;background:#b06a50;color:#fff;padding:.75rem 1.5rem;border-radius:.75rem;font-weight:600;font-size:1rem;text-decoration:none;box-shadow:0 4px 6px -1px rgba(0,0,0,.1)}
[data-hero-image]{display:none;position:relative;aspect-ratio:1/1;background:rgba(255,255,255,.04);border-radius:1.5rem;overflow:hidden;width:100%}
@media (min-width:768px){[data-hero-image]{display:block}}
[data-hero-image] > div[aria-hidden]{position:absolute;inset:0;pointer-events:none}
[data-hero-image] img{position:absolute;inset:0;display:block;width:100%;height:100%;object-fit:cover;object-position:center}
[data-site-logo]{height:68px;width:auto;display:block}
@media (min-width:640px){[data-site-logo]{height:88px}}
@media (min-width:1280px){[data-site-logo]{height:104px}}
.ha-hero-fade{animation:haHeroFade .5s ease-out both}
.ha-hero-fade-delayed{animation:haHeroFade .6s ease-out .15s both}
@keyframes haHeroFade{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){.ha-hero-fade,.ha-hero-fade-delayed{animation:none}}`;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  beforeLoad: ({ location }) => {
    if (isHiddenB2CPath(location.pathname)) {
      throw redirect({ to: "/request-access", replace: true });
    }
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" },
      { title: "Home Approach - Your Guide to Homeownership" },
      {
        name: "description",
        content:
          "Free homeownership tools, expert guides, and education for buyers, owners, sellers, and investors. VIP members earn rewards.",
      },
      { name: "author", content: "Home Approach" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Home Approach" },
      { name: "theme-color", content: "#3a4d6e" },
      { property: "og:title", content: "Home Approach - Free Homeownership Tools & VIP Rewards" },
      {
        property: "og:description",
        content:
          "Master every stage of homeownership with free tools, expert guides, and VIP rewards. Buy, own, sell, or invest -- all in one platform.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.homeapproach.com/" },
      { property: "og:image", content: "https://www.homeapproach.com/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@HomeApproach" },
      { name: "twitter:image", content: "https://www.homeapproach.com/og-image.png" },
    ],
    links: [
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      {
        rel: "alternate",
        type: "application/rss+xml",
        href: "https://www.homeapproach.com/rss.xml",
        title: "Home Approach Blog",
      },
      { rel: "preconnect", href: "https://cuchakuureyyszkukhnm.supabase.co" },
      { rel: "preload", as: "font", type: "font/woff2", href: montserratFontUrl, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: robotoSlabFontUrl, crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "image",
        type: "image/jpeg",
        fetchPriority: "high",
        media: "(min-width: 768px)",
        href: "https://homeapproach.lovable.app/__l5e/assets-v1/5256fcdb-764f-4814-9951-c6b18249256c/hero-porch.jpg",
      },
    ],
    scripts: [
      { children: APP_CSS_ASYNC_SNIPPET },
      { children: GTM_SNIPPET },
      { type: "application/ld+json", children: SITE_JSON_LD },
    ],
    styles: [{ children: CRITICAL_CSS }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundHandler,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

// ported from main.tsx
function ClientInit() {
  useEffect(() => {
    initWebVitals();

    // --- Service worker cleanup ---
    // The kill-switch SW at /sw.js unregisters old Workbox SWs for returning
    // visitors via the browser's automatic SW update polling. In the Lovable
    // preview iframe, proactively unregister everything instead.
    if ("serviceWorker" in navigator) {
      const isInIframe = (() => {
        try {
          return window.self !== window.top;
        } catch {
          return true;
        }
      })();
      const isPreviewHost =
        window.location.hostname.includes("id-preview--") ||
        window.location.hostname.includes("lovableproject.com");
      if (isPreviewHost || isInIframe) {
        navigator.serviceWorker
          .getRegistrations()
          .then((regs) => regs.forEach((r) => r.unregister()))
          .catch(() => {});
      }
    }
  }, []);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LifeMartProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <ScrollToTop />
              <DataGraphPageTracker />
              <AffiliateTracker />
              <AttributionTracker />
              <RouteMetaGuard />
              <ClientInit />
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
              <HomieBubble />
              <MobileBottomNav />
              <MemberPreviewBanner />
            </TooltipProvider>
          </LifeMartProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function NotFoundHandler() {
  const loc = useLocation();
  // Catch typo'd inbound links with an encoded space (e.g. /free-credit-%20score/)
  // -- redirects straight to /check-my-score now rather than bouncing through
  // /free-credit-score, which itself redirects home while that page is disabled.
  const decoded = decodeURIComponent(loc.pathname);
  if (decoded.startsWith("/free-credit- score")) {
    return <Navigate to={`/check-my-score${loc.searchStr ? `?${loc.searchStr}` : ""}`} replace />;
  }
  return <NotFound />;
}

function RootErrorComponent({ error }: { error: unknown }) {
  console.error(error);
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-bold text-foreground">This page didn't load</h1>
        <p className="text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Try again
          </button>
          <a
            href="/"
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}